if (!customElements.get('anatomy-video-carousel')) {
  class AnatomyVideoCarousel extends HTMLElement {
    static videoJsPromise = null;

    constructor() {
      super();
      this.players = [];
      this.playerMap = new Map();
      this.cleanupCallbacks = [];
      this.resizeFrame = null;
      this.lastTouchToggleAt = 0;
      this.handleScroll = this.handleScroll.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handlePlayToggleRequest = this.handlePlayToggleRequest.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handlePrevClick = () => this.scrollByCard(-1);
      this.handleNextClick = () => this.scrollByCard(1);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.track = this.querySelector('[data-carousel-track]');
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.progress = this.querySelector('[data-carousel-progress]');
      this.cards = Array.from(this.querySelectorAll('[data-video-card]'));

      if (!this.viewport || !this.track || this.cards.length === 0) {
        return;
      }

      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
      this.viewport.addEventListener('keydown', this.handleKeyDown);
      this.addEventListener('click', this.handlePlayToggleRequest);
      this.addEventListener('touchend', this.handlePlayToggleRequest, { passive: false });
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.prevButton?.addEventListener('click', this.handlePrevClick);
      this.nextButton?.addEventListener('click', this.handleNextClick);

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.resizeFrame) {
            return;
          }

          this.resizeFrame = requestAnimationFrame(() => {
            this.resizeFrame = null;
            this.updateScrollState();
          });
        });
        this.resizeObserver.observe(this.viewport);
      }

      this.updateScrollState();

      const userAgent = navigator.userAgent || '';
      const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
      const desktopWebKit = !coarsePointer && /AppleWebKit/i.test(userAgent) && !/Chrome|Chromium|CriOS|FxiOS|Edg\//i.test(userAgent);

      if (window.Shopify?.designMode || coarsePointer || desktopWebKit) {
        this.ensurePlayers();
        return;
      }

      if ('IntersectionObserver' in window) {
        const preloadRootMargin = '640px 0px';
        this.sectionObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.sectionObserver.disconnect();
            this.sectionObserver = null;
            this.ensurePlayers();
          }
        }, { rootMargin: preloadRootMargin });
        this.sectionObserver.observe(this);
      } else {
        this.ensurePlayers();
      }
    }

    disconnectedCallback() {
      this.viewport?.removeEventListener('scroll', this.handleScroll);
      this.viewport?.removeEventListener('keydown', this.handleKeyDown);
      this.removeEventListener('click', this.handlePlayToggleRequest);
      this.removeEventListener('touchend', this.handlePlayToggleRequest);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.prevButton?.removeEventListener('click', this.handlePrevClick);
      this.nextButton?.removeEventListener('click', this.handleNextClick);
      this.sectionObserver?.disconnect();
      this.cardObserver?.disconnect();
      this.resizeObserver?.disconnect();

      if (this.scrollFrame) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }

      if (this.resizeFrame) {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }

      this.players.forEach((player) => {
        try {
          player.dispose();
        } catch (error) {
          // Ignore dispose errors during section teardown.
        }
      });

      this.players = [];
      this.playerMap.clear();
      this.playersReady = false;
      this.cleanupCallbacks.forEach((callback) => callback());
      this.cleanupCallbacks = [];
    }

    handleScroll() {
      if (this.scrollFrame) {
        return;
      }

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateScrollState();
      });
    }

    handleKeyDown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }

      event.preventDefault();
      this.scrollByCard(event.key === 'ArrowRight' ? 1 : -1);
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.pauseAll();
      }
    }

    handlePlayToggleRequest(event) {
      if (this.isDuplicateTouchClick(event)) {
        return;
      }

      const playToggle = event.target instanceof Element
        ? event.target.closest('[data-video-play-toggle]')
        : null;

      if (!playToggle) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();

      const card = playToggle.closest('[data-video-card]');
      if (!card) {
        return;
      }

      const player = this.playerMap.get(card);
      if (!player) {
        this.pendingPlayCard = card;
        this.ensurePlayers();
        return;
      }

      this.togglePlayer(player);
    }

    scrollByCard(direction) {
      const firstCard = this.cards[0];

      if (!firstCard || !this.viewport) {
        return;
      }

      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0');
      const amount = firstCard.getBoundingClientRect().width + gap;
      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

      this.viewport.scrollBy({
        left: direction * amount,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    updateScrollState() {
      if (!this.viewport) {
        return;
      }

      const visibleEnd = this.viewport.scrollLeft + this.viewport.clientWidth;
      const maxScroll = Math.max(this.viewport.scrollWidth - this.viewport.clientWidth, 0);
      const progress = this.viewport.scrollWidth > 0 ? Math.min(1, visibleEnd / this.viewport.scrollWidth) : 1;

      if (this.progress) {
        this.progress.style.transform = `scaleX(${progress})`;
      }

      if (this.prevButton) {
        this.prevButton.disabled = this.viewport.scrollLeft <= 4;
      }

      if (this.nextButton) {
        this.nextButton.disabled = maxScroll <= 0 || visibleEnd >= this.viewport.scrollWidth - 4;
      }
    }

    ensurePlayers() {
      if (this.playersReady) {
        return;
      }

      this.playersReady = true;

      this.loadVideoJsCss();

      this.loadVideoJs()
        .then((videojs) => {
          if (!this.isConnected) {
            return;
          }

          this.initPlayers(videojs);
        })
        .catch(() => {
          this.classList.add('avc--fallback');
        });
    }

    loadVideoJsCss() {
      const stylesheetSrc = this.dataset.videojsCss;

      if (!stylesheetSrc || document.querySelector(`link[data-videojs-css][href="${stylesheetSrc}"]`)) {
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = stylesheetSrc;
      link.dataset.videojsCss = 'true';
      document.head.appendChild(link);
    }

    loadVideoJs() {
      if (window.videojs) {
        return Promise.resolve(window.videojs);
      }

      if (AnatomyVideoCarousel.videoJsPromise) {
        return AnatomyVideoCarousel.videoJsPromise;
      }

      const scriptSrc = this.dataset.videojsScript;

      if (!scriptSrc) {
        return Promise.reject(new Error('Missing video.js asset URL'));
      }

      AnatomyVideoCarousel.videoJsPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[data-videojs-core][src="${scriptSrc}"]`);

        if (existingScript) {
          existingScript.addEventListener('load', () => resolve(window.videojs), { once: true });
          existingScript.addEventListener('error', reject, { once: true });
          return;
        }

        const script = document.createElement('script');
        script.src = scriptSrc;
        script.async = true;
        script.dataset.videojsCore = 'true';
        script.onload = () => resolve(window.videojs);
        script.onerror = reject;
        document.head.appendChild(script);
      });

      return AnatomyVideoCarousel.videoJsPromise;
    }

    initPlayers(videojs) {
      const autoplay = this.dataset.autoplay === 'true';
      const loop = this.dataset.loop === 'true';
      const muted = this.dataset.muted !== 'false';
      const fullscreen = this.dataset.fullscreen !== 'false';
      const showPlaybackRates = this.dataset.showPlaybackRates === 'true';
      const hideControlsDelay = parseInt(this.dataset.hideControlsDelay || '2000', 10) || 2000;
      const playbackRates = (this.dataset.playbackRates || '0.75, 1, 1.25, 1.5')
        .split(',')
        .map((value) => parseFloat(value.trim()))
        .filter((value) => !Number.isNaN(value));

      this.cards.forEach((card, index) => {
        const video = card.querySelector('video.video-js');

        if (!video) {
          return;
        }

        const existingPlayer = typeof videojs.getPlayer === 'function' ? videojs.getPlayer(video.id) : null;

        if (existingPlayer) {
          existingPlayer.dispose();
        }

        const player = videojs(video, {
          controls: true,
          autoplay: autoplay && index === 0 ? 'muted' : false,
          loop,
          muted,
          preload: video.getAttribute('preload') || 'metadata',
          fluid: false,
          playsinline: true,
          inactivityTimeout: hideControlsDelay,
          playbackRates: showPlaybackRates ? playbackRates : [],
          userActions: {
            hotkeys: true,
          },
          controlBar: {
            volumePanel: { inline: true },
            pictureInPictureToggle: false,
            fullscreenToggle: fullscreen,
            currentTimeDisplay: true,
            timeDivider: true,
            durationDisplay: true,
            remainingTimeDisplay: false,
            playbackRateMenuButton: showPlaybackRates,
          },
        });

        const playerElement = player.el?.();
        if (playerElement) {
          playerElement.classList.add('avc-card__media');
        }

        player.ready(() => {
          player.el?.().classList.add('avc-card__media');
          player.muted(muted);
          card.classList.add('is-ready');
        });

        this.bindCardPlayback(card, player);

        player.on('play', () => {
          this.pauseAll(player);
          this.cards.forEach((item) => item.classList.remove('is-playing'));
          card.classList.add('is-playing');
        });

        const clearPlaying = () => {
          card.classList.remove('is-playing');
        };

        player.on('pause', clearPlaying);
        player.on('ended', clearPlaying);

        this.players.push(player);
        this.playerMap.set(card, player);
      });

      if ('IntersectionObserver' in window) {
        this.cardObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const player = this.playerMap.get(entry.target);

            if (!entry.isIntersecting && player && !player.paused()) {
              player.pause();
            }
          });
        }, {
          root: this.viewport,
          threshold: 0.65,
        });

        this.cards.forEach((card) => this.cardObserver.observe(card));
      }

      if (this.pendingPlayCard) {
        const pendingPlayer = this.playerMap.get(this.pendingPlayCard);
        if (pendingPlayer) {
          this.playPlayer(pendingPlayer);
        }
        this.pendingPlayCard = null;
      }
    }

    bindCardPlayback(card, player) {
      const playerElement = player.el?.();
      const media = card.querySelector('.avc-card__media') || playerElement || card.querySelector('.video-js');
      const playToggle = card.querySelector('[data-video-play-toggle]');
      const bigPlayButton = player.bigPlayButton?.el?.() || playerElement?.querySelector('.vjs-big-play-button');

      if (!media || !playerElement) {
        return;
      }

      const activatePlayer = (event) => {
        if (this.isDuplicateTouchClick(event)) {
          return;
        }

        if (this.shouldIgnoreToggle(event?.target)) {
          return;
        }

        event?.preventDefault?.();
        this.togglePlayer(player);
      };

      const activateBigPlay = (event) => {
        if (this.isDuplicateTouchClick(event)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        this.playPlayer(player);
      };

      media.addEventListener('click', activatePlayer);
      if (playToggle) {
        playToggle.addEventListener('click', activateBigPlay);
        playToggle.addEventListener('touchend', activateBigPlay, { passive: false });
      }
      if (bigPlayButton) {
        bigPlayButton.addEventListener('click', activateBigPlay);
        bigPlayButton.addEventListener('touchend', activateBigPlay, { passive: false });
      }

      this.cleanupCallbacks.push(() => {
        media.removeEventListener('click', activatePlayer);
        playToggle?.removeEventListener('click', activateBigPlay);
        playToggle?.removeEventListener('touchend', activateBigPlay);
        bigPlayButton?.removeEventListener('click', activateBigPlay);
        bigPlayButton?.removeEventListener('touchend', activateBigPlay);
      });

      const syncPlayToggle = () => {
        if (playToggle) {
          playToggle.setAttribute('aria-pressed', player.paused() ? 'false' : 'true');
        }
      };

      player.on('play', syncPlayToggle);
      player.on('pause', syncPlayToggle);
      player.on('ended', syncPlayToggle);
      syncPlayToggle();
    }

    shouldIgnoreToggle(target) {
      if (!(target instanceof Element)) {
        return false;
      }

      return Boolean(
        target.closest(
          '.avc-card__cta, .vjs-control-bar, .vjs-control, .vjs-menu, .vjs-modal-dialog, a[href], button, input, select, textarea, label'
        )
      );
    }

    playPlayer(player) {
      this.pauseAll(player);
      const playPromise = player.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    togglePlayer(player) {
      if (player.paused()) {
        this.playPlayer(player);
        return;
      }

      player.pause();
    }

    pauseAll(exceptPlayer = null) {
      this.players.forEach((player) => {
        if (player !== exceptPlayer && !player.paused()) {
          player.pause();
        }
      });
    }

    isDuplicateTouchClick(event) {
      if (!event) {
        return false;
      }

      const now = Date.now();

      if (event.type === 'touchend') {
        this.lastTouchToggleAt = now;
        return false;
      }

      if (event.type === 'click' && now - this.lastTouchToggleAt < 450) {
        return true;
      }

      return false;
    }
  }

  customElements.define('anatomy-video-carousel', AnatomyVideoCarousel);
}
